import { defineStore } from 'pinia';
import { ref } from 'vue';

import { TripsApi } from '@apps/main/modules/api/inspire/tripsApi';

import { getDomain } from '@libs/utils';
import type { ChronologicalItinerary } from '@libs/trip-planner-interfaces';
import { isHotelIngredientResponseWithStaticInfo } from '@libs/search-service-smart-search';

import type {
  DisneylandCampaignBannerData,
  DisneylandCampaignModalData,
} from '../models/DisneylandCampaign';
import {
  DISNEYLAND_CAMPAIGN_HOTELS,
  DISNEYLAND_CAMPAIGN_ID,
  DisneylandCampaignMarkets,
} from '../models/DisneylandCampaign';

export const useDisneylandCampaignStore = defineStore(
  'disneylandCampaignStore',
  () => {
    const tripsApi = new TripsApi();

    // State
    const bannerData = ref<DisneylandCampaignBannerData | undefined>();
    const modalData = ref<DisneylandCampaignModalData | undefined>();

    // Actions
    async function tryToFetchCampaignDetails(
      chronologicalItinerary: ChronologicalItinerary[]
    ): Promise<void> {
      const isCorrectMarket =
        getDomain.isSwedishMarket ||
        getDomain.isDanishMarket ||
        getDomain.isNorwegianMarket;

      const dataExists = bannerData.value && modalData.value;
      const chronologicalItineraryHotels = chronologicalItinerary
        .flatMap((itinerary) => itinerary.ingredients)
        .filter(isHotelIngredientResponseWithStaticInfo);
      const hotelCodes: string[] = chronologicalItineraryHotels
        .flatMap((hotel) => [
          hotel.suggested?.code,
          ...(hotel.suggestions?.map((suggestion) => suggestion.code) ?? []),
        ])
        .filter((code): code is string => typeof code === 'string');

      const isTripEligibleForDiscount =
        isThereSomeDisneylandCampaignHotel(hotelCodes);
      if (!isCorrectMarket || !!dataExists || !isTripEligibleForDiscount) {
        return;
      }
      const locale = getDomain.getCurrentDomainSpec().Locale;

      const market =
        DisneylandCampaignMarkets[
          locale as keyof typeof DisneylandCampaignMarkets
        ];

      const data = await tripsApi.fetchCampaignDetails(
        DISNEYLAND_CAMPAIGN_ID,
        market
      );
      bannerData.value = {
        title: data?.headline,
        content: data?.salesPitch,
      };

      modalData.value = {
        title: data?.headline,
        content: data?.salesPitch,
        description: data?.description.portableText,
      };
    }

    function getGiftCardAmount(hotelCode?: string): number | undefined {
      if (!hotelCode) {
        return undefined;
      }
      return DISNEYLAND_CAMPAIGN_HOTELS.find((hotel) =>
        hotel.hotelCodes.includes(hotelCode)
      )?.giftCardValue;
    }

    function isDisneylandCampaignHotel(hotelCode?: string): boolean {
      if (!hotelCode) {
        return false;
      }
      return DISNEYLAND_CAMPAIGN_HOTELS.some((hotel) =>
        hotel.hotelCodes.includes(hotelCode)
      );
    }

    function isThereSomeDisneylandCampaignHotel(hotelCodes: string[]): boolean {
      return hotelCodes.some((code) =>
        DISNEYLAND_CAMPAIGN_HOTELS.some((hotel) =>
          hotel.hotelCodes.includes(code)
        )
      );
    }

    return {
      // State
      bannerData,
      modalData,

      // Actions
      tryToFetchCampaignDetails,
      getGiftCardAmount,
      isDisneylandCampaignHotel,
      isThereSomeDisneylandCampaignHotel,
    };
  }
);
